import { MODELS, useModel } from "../../../../api";
import NotAuthorizedPage from "../../../auth/UnauthorizedView/NotAuthorizedPage.jsx";
import { useSparkContext } from "../../context/SparkContext.jsx";
import styles from "./TeachersOverview.css.ts";
import TeacherList from "./components/TeacherList/TeacherList.jsx";
import TeacherInviteForm from "./components/TeacherInviteForm/TeacherInviteForm.jsx";
import PendingTeacherList from "./components/PendingTeacherList/PendingTeacherList.jsx";

/**
 * TeachersOverviewWithData component displays an overview of teachers
 * including a list of current teachers, a steps to invite new teachers,
 * and a list of pending teacher invites.
 *
 * @param {Object} props - Component properties.
 * @param {Object} props.workshop - The workshop information.
 * @param {Object[]} props.pendingInvites - The list of pending teacher invites.
 * @returns {JSX.Element} The rendered component.
 */
const TeachersOverviewWithData = ({ workshop, pendingInvites }) => {
  return (
    <div className={styles.teachersPage}>
      <div className={styles.containerTop}>
        <TeacherInviteForm workshopId={workshop.id} />
      </div>

      <div className={styles.containerBottom}>
        <PendingTeacherList teachers={pendingInvites} />
        <TeacherList workshop={workshop} />
      </div>
    </div>
  );
};

/**
 * `TeachersOverview` is a functional component that renders an overview
 * page for teachers in the context of a specified workshop and spark.
 *
 * This component uses the parameters `workshopSlug` and `sparkSlug` from
 * the route, and fetches the associated workshop and its pending invites.
 * It handles different loading states, authorization, and displays appropriate
 * content based on the user's role and the fetched data.
 *
 * It renders a custom navigation header and conditionally displays a spinner,
 * a not found page, a not authorized page, or the main content.
 */
const TeachersOverview = () => {
  const { workshop } = useSparkContext();
  const pendingInvites = useModel(MODELS.WORKSHOP_PENDING_INVITES(workshop.id));

  // If I am not a teacher of this course or an admin, return not allowed.
  if (
    !(workshop.roles?.includes("teacher") || workshop.roles?.includes("admin"))
  ) {
    return <NotAuthorizedPage />;
  }

  return (
    <TeachersOverviewWithData
      workshop={workshop}
      pendingInvites={pendingInvites}
    />
  );
};

export default TeachersOverview;
